import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { designSystem, FloatingAction, Modal, Panel, PanelGroup } from '@yola/ws-ui';
import Breadcrumbs from 'src/js/modules/common/components/breadcrumbs';
import generatePresetLabel from '../helpers/generate-preset-label';
import constants from '../constants';
import ContentBox from './content-box';
import ColorPalettePreset from './color-palette-preset';

const {
  DialogHeader,
  Box,
  Stack,
  DialogButtonGroup,
  DialogButton,
  Paragraph,
  Heading,
  DialogButtonGroupSeparator,
} = designSystem;

const {
  DEFAULT_OFFSET_Y,
  DEFAULT_OFFSET_X,
  DEFAULT_DIALOG_HEIGHT,
  DEFAULT_DIALOG_WIDTH,
  DEFAULT_DIALOG_MIN_HEIGHT,
} = constants;

function DesignColorsDialog({
  captions,
  palettes,
  customPalettes,
  activePaletteId,
  setActivePalette,
  onEditPaletteClick,
  onDeletePaletteClick,
  onAddPaletteClick,
  offsetX = DEFAULT_OFFSET_X,
  offsetY = DEFAULT_OFFSET_Y,
  onSubmit,
  onDragEnd,
  onCancel,
}) {
  const hasCustomPalettes = customPalettes.length > 0;

  const getPaletteLabel = (palette, isCustom) => {
    if (isCustom) {
      return `${captions.customPaletteLabel} ${palette.labelNumber}`;
    }

    return generatePresetLabel(captions.paletteLabel, palette.id);
  };

  const renderPalettes = (palettesList, isCustom = false) =>
    palettesList.map((palette) => (
      <ColorPalettePreset
        key={palette.id}
        id={palette.id}
        title={getPaletteLabel(palette, isCustom)}
        editLabel={captions.editLabel}
        deleteLabel={captions.deleteLabel}
        colors={palette.colors}
        isSelected={palette.id === activePaletteId}
        isCustom={isCustom}
        onClick={setActivePalette}
        onEditClick={onEditPaletteClick}
        onDeleteClick={onDeletePaletteClick}
      />
    ));

  return (
    <Modal
      draggable
      resizable
      fullscreen={bowser.mobile}
      offsetX={offsetX}
      offsetY={offsetY}
      width={DEFAULT_DIALOG_WIDTH}
      minWidth={DEFAULT_DIALOG_WIDTH}
      maxWidth={DEFAULT_DIALOG_WIDTH}
      minHeight={DEFAULT_DIALOG_MIN_HEIGHT}
      height={DEFAULT_DIALOG_HEIGHT}
      dragHandleSelector=".ws-drag-trigger"
      dragCancelSelector=".ws-ui-dialog-header-controls"
      overlay="none"
      resizeIcon="ns-resize"
      handleCancel={onCancel}
      handleSubmit={onSubmit}
      onDragEnd={onDragEnd}
      onOverlayClick={onSubmit}
    >
      <PanelGroup height="100%">
        <Panel
          align="middle"
          corners="squared"
          className="ws-drag-trigger"
          style={{ flexShrink: '0' }}
        >
          <DialogHeader paddingBottom="spacing-s">
            <Box>
              <Box paddingBottom="spacing-2-xs">
                <Breadcrumbs breadcrumbs={captions.breadcrumbs} />
              </Box>
              <Box>
                <Stack>
                  <Heading type="heading-3">{captions.title}</Heading>
                  <Paragraph>{captions.description}</Paragraph>
                </Stack>
              </Box>
            </Box>
          </DialogHeader>
        </Panel>
        <Panel height="100%" scrollable style={{ borderBottom: 'none' }}>
          <ContentBox>
            {hasCustomPalettes && (
              <Fragment>
                <Box marginBottom="spacing-2-xs">
                  <Heading type="heading-6">{captions.customPalettesTitle}</Heading>
                </Box>
                <Stack gap="spacing-xs">{renderPalettes(customPalettes, true)}</Stack>
                <Box marginTop="spacing-m" marginBottom="spacing-2-xs">
                  <Heading type="heading-6">{captions.heading}</Heading>
                </Box>
              </Fragment>
            )}
            <Stack gap="spacing-xs">{renderPalettes(palettes)}</Stack>
            <FloatingAction
              icon="plus"
              inverseOffsetOrigin
              offsetX={24}
              offsetY={70}
              onClick={onAddPaletteClick}
            />
          </ContentBox>
        </Panel>
        <Panel corners="squared" style={{ flexShrink: '0' }}>
          <DialogButtonGroup>
            <DialogButton onClick={onSubmit}>{captions.submitButton}</DialogButton>
            <DialogButtonGroupSeparator />
            <DialogButton onClick={onCancel}>{captions.cancelButton}</DialogButton>
          </DialogButtonGroup>
        </Panel>
      </PanelGroup>
    </Modal>
  );
}

DesignColorsDialog.propTypes = {
  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
  captions: PropTypes.shape({
    breadcrumbs: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string,
    description: PropTypes.string,
    heading: PropTypes.string,
    editLabel: PropTypes.string,
    deleteLabel: PropTypes.string,
    paletteLabel: PropTypes.string,
    customPalettesTitle: PropTypes.string,
    customPaletteLabel: PropTypes.string,
    submitButton: PropTypes.string,
    cancelButton: PropTypes.string,
  }).isRequired,
  palettes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      colors: PropTypes.shape().isRequired,
      variables: PropTypes.shape().isRequired,
    })
  ).isRequired,
  customPalettes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      colors: PropTypes.shape().isRequired,
      labelNumber: PropTypes.number.isRequired,
    })
  ).isRequired,
  activePaletteId: PropTypes.string.isRequired,
  setActivePalette: PropTypes.func.isRequired,
  onEditPaletteClick: PropTypes.func.isRequired,
  onDeletePaletteClick: PropTypes.func.isRequired,
  onAddPaletteClick: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default DesignColorsDialog;
